import { RENEWABLE_AUTH_ERROR_CODES, PaymentApiErrorCode } from '@mfw/common';

import BaseApiResponseError from '../api/http/BaseApiResponseError';
import UnprocessableEntity from '../api/http/UnprocessableEntity';
import UnauthorizedError from '../api/http/UnauthorizedError';
import NetworkError from '../api/http/NetworkError';
import { isRefusedRenewalError } from '../api/http/isRefusedRenewalError';
import { isRenewalFailure } from '../api/http/renewalFailure';

/**
 * Which API failures deserve a red toast.
 *
 * Extracted from `AuthProvider`'s two cache subscriptions so it can be tested at all. The
 * rule that matters is the third one: a stale session cookie is renewed and the request
 * replayed by the Axios interceptor, so the user must not be told about it — and after a
 * wake-up that would not be one toast but one per query the reconnect refetched. That
 * burst is the bug the tester actually reported.
 */
export function shouldToastApiError(
  error: unknown,
  options?: {
    /**
     * Where the failure came from. A `mutation` is something the user just
     * asked for by hand, so it is never silenced below; a `query` is a
     * background read the user did not ask for and may not even be looking at.
     */
    source?: 'mutation' | 'query';
  },
) {
  if (!(error instanceof BaseApiResponseError)) return false;
  // Form validation renders field errors of its own.
  if (error instanceof UnprocessableEntity) return false;
  // §16 (R-AUD-62), the third burst of the same shape: an account with a
  // billing-locked organization fires one of these per background list — nine
  // on the personal Home alone — and the sentence names no organization, so
  // even one of them cannot be acted on. The state has two surfaces that CAN
  // name it (the billing banner and the org lock screen), which is where the
  // user is told. A WRITE keeps its toast: silence on a button the user just
  // pressed reads as a broken button.
  if (
    error.code === PaymentApiErrorCode.ORG_BILLING_LOCKED &&
    options?.source === 'query'
  ) {
    return false;
  }
  // auth-11 P2-2, the fourth burst of the same shape and the one with no code to name it:
  // since PR-2 an unreachable mint reaches the caller as its own uncoded failure. The guard
  // shares one mint between every caller that joined it and rethrows that same error for
  // the whole 30s cooldown, so nine sleeping queries meant nine red boxes about one outage
  // — one the guard has already reported once, or deliberately kept quiet about because it
  // was pure connectivity. That single report is the surface for a READ. A write keeps its
  // toast, as `ORG_BILLING_LOCKED` does: silence on a button the user just pressed reads as
  // a broken button.
  if (isRenewalFailure(error) && options?.source === 'query') return false;
  // The fifth burst of the same shape, and the plainest: a request that went out and got
  // nothing back. It is not an answer, so it says nothing the user can act on — it does not
  // even name what failed — and the request carrying it is usually a poll the user never
  // started, landing on whatever screen happens to be open. That was the tester's report:
  // a red box on random screens. What the failure IS worth is a Sentry event naming the
  // endpoint, which `reportNetworkFailure` mints at the moment the error is built. A write
  // keeps its toast, as the two rules above do: silence on a button the user just pressed
  // reads as a broken button, or worse, as a save that went through.
  if (error instanceof NetworkError && options?.source === 'query')
    return false;
  // The two identity refusals are silent for the OPPOSITE reason to the renewable codes
  // below: neither is replayed, each ends the session, and what the user gets is the login
  // screen — not a red box per failed query. `SESSION_EXPIRED` is answered by ONE renewal
  // first (Axios layer); what reaches here after PR-2 is the renewal's own refusal, so it
  // is silent for the same terminal reason.
  if (isSessionOverError(error)) return false;
  // Silent even when the REPLAY was refused with it: that case is not lost, the Axios layer
  // hands it to the session guard, which reports it once (toast + Sentry) and paces the
  // retries — a toast per query here would be the wake-up burst all over again. The same
  // list the replay layer branches on, so the two cannot answer differently about one code.
  return !RENEWABLE_AUTH_ERROR_CODES.some(
    (renewable) => renewable === error.code,
  );
}

/**
 * Whether a mutation opted out of the global error toast because it shows its own
 * error UI: `meta.suppressErrorToast` is `true`, or a predicate over the error (e.g.
 * a seat-cap dialog instead of a generic toast). The variables ride along so a
 * predicate can scope its verdict to how the mutation was called — `useLoggedUser`
 * suppresses the listener's renewal but not the post-mutation refetch — and a
 * predicate reading only the error ignores them.
 *
 * One reading for both places that must agree on it: the `AuthProvider` subscription
 * that decides whether to toast, and the `Dialog` confirm that decides whether the
 * user was told at all.
 */
export function isErrorToastSuppressed(mutation: {
  options: { meta?: Record<string, unknown> };
  state: { error: unknown; variables: unknown };
}) {
  const suppressErrorToast = mutation.options.meta?.suppressErrorToast;
  return (
    suppressErrorToast === true ||
    (typeof suppressErrorToast === 'function' &&
      (suppressErrorToast as (error: unknown, variables: unknown) => boolean)(
        mutation.state.error,
        mutation.state.variables,
      ))
  );
}

/**
 * The query-side opt-out, read the same way: `meta.suppressErrorToast` is `true`, or a
 * predicate over `(error, query, actionType)`. The action rides along because the cache
 * reports a query's error again on every later state change while it lasts (a refetch
 * starting, an invalidation) — a predicate that wants to speak once per failure needs to
 * tell the `'error'` dispatch from those echoes. A query without the meta key keeps
 * exactly the behaviour it had.
 *
 * Read by the `AuthProvider` toast subscription and by `handleGlobalQueryError`, so a
 * polled read that opted out of repeats is neither toasted nor reported once per poll.
 */
export function isQueryErrorToastSuppressed(
  query: { meta?: Record<string, unknown>; state: { error: unknown } },
  actionType?: string,
) {
  const suppressErrorToast = query.meta?.suppressErrorToast;
  return (
    suppressErrorToast === true ||
    (typeof suppressErrorToast === 'function' &&
      Boolean(
        (
          suppressErrorToast as (
            error: unknown,
            query: unknown,
            actionType?: string,
          ) => unknown
        )(query.state.error, query, actionType),
      ))
  );
}

/**
 * Which answers mean the session is over and the app must sign the user out.
 *
 * A 401 has always been that trigger. Everything else is delegated to
 * `isRefusedRenewalError`: `TENANT_MISMATCH` and `ACCOUNT_SUSPENDED` (sen-5 F7) are terminal
 * outright — the guard mints them only for a verified identity it will not bind to an
 * account here, or for an account that is archived or deleted, a state no renewal changes.
 * `SESSION_EXPIRED` is answered by ONE renewal first (Axios layer); what reaches the cache
 * subscribers after PR-2 is the renewal's own refusal, so by the time it gets here it is
 * terminal too. The two Firebase codes on which the SDK signs itself out locally join for
 * the same reason: the rejection reaches the guard first, and it must call them terminal
 * rather than an outage. The renewable codes are deliberately NOT here — `INVALID_TOKEN`
 * (the cookie's token no longer verifies) and `COOKIE_REQUIRED` (the browser dropped the
 * cookie at its own deadline) are both answered by a renewal and a replay, and signing out
 * on either would log out every tab that merely fell asleep or outlived a cookie the
 * Firebase identity can still replace. An UNCODED 403 stays out too: it is also what the
 * guard answers when its own lookups fail, and a database blink must never end a session.
 */
export function isSessionOverError(error: unknown) {
  if (error instanceof UnauthorizedError) return true;
  return isRefusedRenewalError(error);
}
